import { type TransitionState } from '@atlassian/state-machine/types';
import type AnalyticsWebClient from '@atlassiansox/analytics-web-client';
import EventEmitter from 'eventemitter3';

// todo: https://product-fabric.atlassian.net/browse/CNS-10273 - Remove dependency
// eslint-disable-next-line no-restricted-imports
import { currentSessionId } from '@canvas/canvas-state/analytics';
import { type CollabInfrastructure } from '@canvas/document-id';
import {
  MetricName,
  ReliabilityMetrics,
  type MetricNameValue,
} from '@canvas/frontend-metrics';

import { type CollabStrategy } from '../collab-strategy/index.ts';
import {
  type PendingMessage,
  type SocketMachineContext,
} from '../state-machine/network-state-machine-context.ts';
import {
  type Events,
  type RestartEvent,
} from '../state-machine/network-state-machine-events.ts';
import {
  createNetworkStateMachine,
  type SocketStateMachine,
  type State,
} from '../state-machine/network-state-machine.ts';
import { untilInState } from '../state-machine/state-machine-utils.ts';
import { type CollabConnectionClientSender } from '../transport/collab-connection-transport.ts';
import type { TransportCallbacks } from '../transport/transport.ts';

import { type RemoteDocument } from './document.ts';
import { WsSocketError } from './errors.ts';
import { Queue } from './queue.ts';
import { SocketReconnectDebounce, type Waiter } from './socket-reconnect-debounce.ts';
import { type StateBridge } from './state-bridge.ts';
import { WebSocketCodes } from './websocket-codes.ts';

export interface YjsSocketEvents {
  diagnostic: (event: SocketDiagnostic) => void;
  message: (
    buffer: Uint8Array<ArrayBuffer>,
    sender?: CollabConnectionClientSender,
  ) => void;
  transientMessage: (
    buffer: Uint8Array<ArrayBuffer>,
    sender?: CollabConnectionClientSender,
  ) => void;
  connected: () => void;
  disconnected: () => void;
  failingRetry: (error: Error) => void;
  connecting: (attempt: number) => void;
}

export type SocketDiagnostic =
  | { kind: 'close'; code: number | null }
  | { kind: 'restart'; reason: RestartEvent['reason'] }
  | { kind: 'attempt'; attempt: number }
  | { kind: 'pause' | 'resume' | 'destroy' }
  | {
      kind: 'failure';
      reason: 'transport-error' | 'operation-failed' | 'connection-timeout';
      phase: 'authentication' | 'document-sync' | 'transport' | 'reconnect';
    };

export interface RelaySupplierResult {
  /**
   * The URL to connect to.
   */
  url: string;
  /**
   * The socket.io namespace to connect to.
   */
  namespace?: string;
  /**
   * Extra headers to pass to the transport (e.g. manually setting cookies in collab-sim).
   */
  extraHeaders?: Record<string, string>;
}

export interface AuthenticatedSocketOptions {
  accessTokenSupplier: () => Promise<string>;
  relaySupplier: (
    attempt: number,
    signal: AbortSignal,
    collabStrategy: CollabStrategy,
  ) => RelaySupplierResult;
  onSocketFetchSnapshot: (signal: AbortSignal) => Promise<RemoteDocument>;
  onSocketFetchSteps: (
    remoteDocument: RemoteDocument,
    attempt: number,
    signal: AbortSignal,
  ) => Promise<RemoteDocument>;
  onSocketApplyChanges: (relayUrl: string, remoteDoc: RemoteDocument) => RemoteDocument;
  onSocketUpdateChanges?: (
    remoteDocument: RemoteDocument,
    connectionAttempts: number,
    signal: AbortSignal,
  ) => void;
  reconcileOfflineChanges?: (remoteDocument: RemoteDocument) => Promise<void>;
  /**
   * Triggered when the socket is closed. Reconnects may still take place after
   * it has closed. Used this hook to execute logic after a socket close event.
   */
  onSocketClosed?: (reason: WebSocketCodes) => void;
  onAckTimeout?(): void;
  onMessageSent?: TransportCallbacks['onMessageSent'];
  debounceWaiter?: Waiter;
  shouldHaltRetry: () => boolean;
  socketFactory?: SocketFactory;
  getAnalyticsWebClient?: () => Promise<AnalyticsWebClient>;
  stateBridge?: StateBridge;
  signal?: AbortSignal;
  /**
   * Whether the state machine should skip fetching steps and applying changes.
   * This is useful in cases like version history where we don't want to apply
   * the latest changes.
   */
  shouldSkipUpdates: () => boolean;
}

export interface SocketInterface extends EventEmitter<YjsSocketEvents> {
  send(data: Uint8Array<ArrayBufferLike>, enqueue?: boolean): void;
  sendToPeer(data: Uint8Array<ArrayBufferLike>, peer: string): void;
  sendAwareness(data: Uint8Array<ArrayBufferLike>): void;
  clearPendingMessages(): void;
  skipGracePeriod(): void;
  destroy(): void;
  connect(): Promise<void>;
  pause(): void;
  resume(): void;
  restart(reason: RestartEvent['reason']): void;
  resetReconnectBackoff(): void;
  flushTransport(ignoreAck?: boolean): void;
  open: boolean;
  state: State;
  collabInfrastructure: CollabInfrastructure | undefined;
}

export type SocketFactory = (url: string) => Promise<WebSocket> | WebSocket;

export class AuthenticatedSocket
  extends EventEmitter<YjsSocketEvents>
  implements SocketInterface
{
  private stateMachine: SocketStateMachine;
  private controller: AbortController = new AbortController();
  private messages: Queue<PendingMessage> = new Queue<PendingMessage>();
  private readonly onSocketClosed: (reason: WebSocketCodes) => void;

  constructor({
    accessTokenSupplier,
    relaySupplier,
    onSocketFetchSnapshot,
    onSocketFetchSteps,
    onSocketApplyChanges,
    onSocketUpdateChanges,
    debounceWaiter,
    shouldHaltRetry,
    signal,
    onSocketClosed,
    shouldSkipUpdates,
    reconcileOfflineChanges,
    getAnalyticsWebClient,
    stateBridge,
    onAckTimeout,
    onMessageSent,
  }: AuthenticatedSocketOptions) {
    super();
    this.onSocketClosed = onSocketClosed ?? (() => {});

    this.stateMachine = createNetworkStateMachine({
      sessionId: currentSessionId.sessionId,
      getAnalyticsWebClient,
      stateBridge,
      socketFetchSnapshot: onSocketFetchSnapshot,
      socketFetchSteps: onSocketFetchSteps,
      socketApplyChanges: onSocketApplyChanges,
      socketUpdateChanges: onSocketUpdateChanges ?? (() => {}),
      socketConnect: this.onSocketConnect,
      onAckTimeout,
      onMessageSent,
      accessTokenSupplier,
      relaySupplier: relaySupplier,
      debounce: new SocketReconnectDebounce(
        (error) => this.emit('failingRetry', error),
        debounceWaiter,
      ),
      signal: this.controller.signal,
      shouldHaltRetry,
      pendingMessages: this.messages,
      shouldSkipUpdates,
      reconcileOfflineChanges,
    });

    this.stateMachine.onEvent(this.onStateMachineEvent);
    this.stateMachine.onTransition(this.onStateMachineTransition);
    signal?.addEventListener('abort', this.destroy, {
      signal: this.controller.signal,
      once: true,
    });
  }

  private wsConnectStartTime: number | null = null;

  private wsConnectMetricName(): MetricNameValue {
    return this.collabInfrastructure === 'ncs'
      ? MetricName.WEBSOCKET_CONNECT_NCS
      : MetricName.WEBSOCKET_CONNECT;
  }

  private successConnectionMetric = () => {
    const startedAt = this.wsConnectStartTime;
    if (startedAt === null) {
      return;
    }
    ReliabilityMetrics.getInstance().successWithDuration(
      this.wsConnectMetricName(),
      startedAt,
      Date.now(),
    );
  };

  private recordConnectionFailure(ev: Events) {
    const startedAt = this.wsConnectStartTime;
    if (startedAt === null) {
      return;
    }
    ReliabilityMetrics.getInstance().failureWithDuration(
      this.wsConnectMetricName(),
      startedAt,
      Date.now(),
      { errorMessage: this.socketEventFailureMessage(ev) },
    );
  }

  private socketEventFailureMessage(ev: Events): string {
    switch (ev.type) {
      case 'error': {
        const wsError =
          ev.error instanceof WsSocketError
            ? (ev.error.event as ErrorEvent)?.error
            : null;
        return (
          (wsError instanceof Error ? wsError.message : null) ??
          ev.error?.message ??
          'websocket error'
        );
      }
      case 'close':
        return `websocket closed: code ${ev.code}`;
      case 'timeout':
      case 'gracePeriodTimeout':
        return ev.error.message;
      default:
        return `connection error: ${ev.type}`;
    }
  }

  // authentication/token errors, network connectivity errors, connection timeout errors should abort
  private shouldAbortForClientError = (ev: Events): boolean => {
    if (ev.type !== 'error' || !ev.error?.message) {
      return false;
    }

    const errorMessage = ev.error.message.toLowerCase();

    if (errorMessage.includes('could not fetch token after multiple attempts')) {
      return true;
    }

    if (
      errorMessage.includes('failed to fetch') ||
      errorMessage.includes('networkerror when attempting to fetch resource')
    ) {
      return true;
    }

    if (errorMessage.includes('connection timeout')) {
      return true;
    }

    return false;
  };

  private onSocketConnect = () => {
    // This needs to happen here and not onStateMachineEvent or onStateMachineTransition,
    // since we have additional logic to determine if the WebSocket has actually
    // connected successfully in the state machine.
    //
    // The state machine will call this method when it has determined that the WebSocket
    // is ready to send messages.
    const event = 'connected';
    this.successConnectionMetric();
    this.emit(event);
  };

  private emitDiagnostic(event: SocketDiagnostic): void {
    try {
      this.emit('diagnostic', event);
    } catch {
      // An optional diagnostic listener cannot interrupt connection state transitions.
    }
  }

  private onStateMachineEvent = (ev: Events) => {
    switch (ev.type) {
      case 'messageReceived':
        this.emit('message', ev.message, ev.sender);
        break;
      case 'transientMessageReceived':
        this.emit('transientMessage', ev.message, ev.sender);
        break;
      case 'close':
        this.emit('disconnected');
        this.emitDiagnostic({
          kind: 'close',
          code:
            Number.isInteger(ev.code) && ev.code >= 1000 && ev.code <= 4999
              ? ev.code
              : null,
        });
        this.onSocketClosed(ev.code);
        break;
      case 'debounceComplete':
        this.emit('connecting', this.stateMachine.context().debounce.attempt);
        this.emitDiagnostic({
          kind: 'attempt',
          attempt: this.stateMachine.context().debounce.attempt,
        });
    }
  };

  private onStateMachineTransition = (
    ev: Events,
    ctx: SocketMachineContext,
    { current, previous }: TransitionState<State>,
  ) => {
    if (current === previous) {
      return;
    }

    switch (ev.type) {
      case 'restart':
        if (
          (current === 'closed' && previous === 'connected') ||
          current === 'connecting:error'
        ) {
          this.emitDiagnostic({ kind: 'restart', reason: ev.reason });
        }
        break;
      case 'pause':
        if (current === 'connecting:paused') {
          this.emitDiagnostic({ kind: 'pause' });
        }
        break;
      case 'resume':
        if (previous === 'connecting:paused') {
          this.emitDiagnostic({ kind: 'resume' });
        }
        break;
      case 'error':
      case 'rejected':
      case 'timeout':
        if (
          (current === 'closed' && previous === 'connected') ||
          current === 'connecting:error'
        ) {
          this.emitDiagnostic({
            kind: 'failure',
            reason:
              ev.type === 'timeout'
                ? 'connection-timeout'
                : ev.type === 'error'
                  ? 'transport-error'
                  : 'operation-failed',
            phase:
              previous === 'connecting:authenticating'
                ? 'authentication'
                : previous.startsWith('connecting:syncing-') ||
                    previous === 'connecting:reconcile-offline-changes'
                  ? 'document-sync'
                  : previous === 'connected' ||
                      previous === 'connecting:starting-socket' ||
                      previous === 'connecting:grace-period'
                    ? 'transport'
                    : 'reconnect',
          });
        }
        break;
    }

    switch (current) {
      case 'connecting:error': {
        const isSessionLimitError =
          (ev.type === 'error' && ev.error?.name === 'WSConnectionLimitExceededError') ||
          (ev.type === 'close' && ev.code === WebSocketCodes.SESSION_FULL);

        const isClientSideError = this.shouldAbortForClientError(ev);

        const connectStart = this.wsConnectStartTime;
        if (connectStart === null) {
          break;
        }
        if (isSessionLimitError) {
          ReliabilityMetrics.getInstance().abortWithDuration(
            this.wsConnectMetricName(),
            connectStart,
            Date.now(),
            { reason: 'session_limit_reached' },
          );
        } else if (isClientSideError) {
          ReliabilityMetrics.getInstance().abortWithDuration(
            this.wsConnectMetricName(),
            connectStart,
            Date.now(),
            {
              reason: 'client_side_error',
              errorMessage: ev.type === 'error' ? (ev.error?.message ?? '') : '',
              state: previous,
            },
          );
        } else {
          this.recordConnectionFailure(ev);
        }
        this.stateMachine.context().debounce.captureErrorState(previous);
        break;
      }
    }

    const details = {
      traceId: ctx.traceId,
      sessionId: ctx.sessionId,
      collabInfrastructure: this.collabInfrastructure ?? 'cf',
      event: ev.type,
      ...(ev.type === 'error' || ev.type === 'rejected'
        ? {
            error: ev.error,
          }
        : {}),
      ...(ev.type === 'restart' ? { reason: ev.reason } : {}),
    };
    console.debug(`[collab] socket transition from ${previous} to ${current}`, details);
  };

  public async connect() {
    if (this.controller.signal.aborted) {
      // Don't attempt to connect if connection is aborted
      return;
    }

    // Always reset wsConnectStartTime on each connect() call so metric timings are accurate
    // even if connect() is called when state is not 'init' (e.g. after manual disconnect).
    this.wsConnectStartTime = Date.now();

    if (this.stateMachine.state() === 'init') {
      this.stateMachine.send({
        type: 'start',
      });
    }

    const state = await untilInState(this.stateMachine, 'connected', 'init');
    const context = this.stateMachine.context();

    if (state === 'closed' && context.transport) {
      throw context.error ?? new Error('Encountered error creating socket');
    }
  }

  public get open() {
    return this.stateMachine.state() === 'connected';
  }

  public send(message: Uint8Array<ArrayBuffer>, enqueue = false) {
    this.messages.enqueue({
      message,
      enqueue,
    });
    this.stateMachine.send({
      type: 'send',
    });
  }

  public sendToPeer(message: Uint8Array<ArrayBuffer>, peer: string) {
    const ctx = this.stateMachine.context();
    if (ctx.transport?.sendToPeer) {
      return ctx.transport.sendToPeer(message, peer);
    }
    throw new Error('sendToPeer called in CF mode');
  }

  public sendAwareness(message: Uint8Array<ArrayBuffer>) {
    const ctx = this.stateMachine.context();
    // remote document is always set at this point
    // we still do a .? for defensive programming
    ctx.remoteDocument?.collabStrategy.sendAwareness(message, ctx.transport, (msg) =>
      this.send(msg),
    );
  }

  /**
   * Clear all pending messages in the queue without sending them.
   */
  public clearPendingMessages() {
    this.messages.dequeueAll();
  }

  /**
   * Signal the state machine to skip the grace period on the next connection,
   * transitioning directly from starting-socket to connected when the socket opens.
   * Used when there is a pending offline diff to send — the reconnect is intentional
   * and we don't need to wait for NCS to potentially close the connection.
   */
  public skipGracePeriod() {
    this.stateMachine.context().skipGracePeriod = true;
  }

  /**
   * Temporarily disconnect the socket and wait until resume is called
   */
  public pause = () => {
    this.stateMachine.send({
      type: 'pause',
    });
  };

  public resume = () => {
    this.stateMachine.send({
      type: 'resume',
    });
  };

  /**
   * Permanently socket and cleanup listeners
   */
  public destroy = () => {
    this.controller.abort();
    this.emit('disconnected');
    this.emitDiagnostic({ kind: 'destroy' });
  };

  public restart = (reason: RestartEvent['reason']) => {
    this.stateMachine.send({
      type: 'restart',
      reason,
    });
  };

  /**
   * Reset the reconnect debounce so the next reconnect attempt happens
   * immediately with no accumulated backoff. Used when a close is expected to
   * be transient and recoverable (e.g. SESSION_LOCKED during a board migration),
   * so that when the board is unlocked the client reconnects promptly rather
   * than waiting out a grown exponential-backoff delay (up to 60s).
   */
  public resetReconnectBackoff = () => {
    this.stateMachine.context().debounce.resetAttempts();
  };

  get state() {
    return this.stateMachine.state();
  }

  get collabInfrastructure() {
    return this.stateMachine.context().remoteDocument?.collabStrategy.type;
  }

  public flushTransport(ignoreAck?: boolean) {
    this.stateMachine.context().transport?.flush(ignoreAck);
  }
}
